// src/admin/AdminDashboard.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Hero from "../../components/ui/Hero.jsx";
import Card from "../../components/ui/Card.jsx";
import TypeBadge from "../../components/ui/TypeBadge.jsx";
import { Skeleton, KpiCardSkeleton, TableRowSkeleton, TableSkeleton } from "../../components/ui/Skeleton.jsx";
import { listApplications } from "../../api/borrowerPortal/applications.js";
import { getMortgageProducts } from "../../api/lenderPortal/listedproducts.js";
import { getAllProductSummary } from "../../api/lenderPortal/mortgageSummary.js";
import { getRecentActivities } from "../../api/admin/recentActivities.js";
import adminListUsers from "../../api/admin/adminListUsers.js";
import { apiGet, extractMessage } from "../../api/client.js";
import {
  Package,
  Users,
  ClipboardList,
  Clock,
  Filter,
  ChevronDown,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ArrowRight,
  Calendar,
  Zap,
  Activity,
  AlertCircle,
  Building2,
  CheckCircle,
} from "lucide-react";

// Money formatter
const money = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && v.startsWith("£")) return v;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(Number(v));
  } catch {
    return String(v);
  }
};

// Helper function to normalize application data from backend
function normalizeApplication(app) {
  try {
    const payload = typeof app.application_payload === 'string' 
      ? JSON.parse(app.application_payload) 
      : app.application_payload || {};
    
    const borrowerName = app.first_name && app.surname 
      ? `${app.first_name} ${app.surname}` 
      : payload.first_name && payload.surname
      ? `${payload.first_name} ${payload.surname}`
      : app.applicant || "Unknown";
    
    return {
      id: app.name || app.id,
      borrower: {
        name: borrowerName,
        email: app.applicant || payload.email || app.email || "",
        phone: app.phone_number || payload.phone_number || "",
      },
      productType: payload.btl?.borrowerType || app.product_type || "Buy-to-Let",
      product: {
        productId: app.mortgage_product || payload.mortgage_product || "—",
        name: app.product_name || "—",
        lender: app.lender_name || "—",
        rate: app.product_rate_percent || 0,
      },
      loanAmount: app.loan_amount || payload.loan_amount || 0,
      status: app.status || "Pending",
      statusTone: getStatusTone(app.status),
      lastUpdated: app.modified ? new Date(app.modified) : new Date(),
      modified: app.modified ? new Date(app.modified) : new Date(),
      priority: "medium",
      assignedTo: app.team_member || app.assigned_to || "Unassigned",
    };
  } catch (error) {
    console.error("Error normalizing application:", error);
    return null;
  }
}

function getStatusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("complete") || s.includes("approved")) return "green";
  if (s.includes("reject") || s.includes("decline")) return "red";
  if (s.includes("valuation") || s.includes("legals")) return "yellow";
  return "blue";
}

function formatRelativeTime(date) {
  const now = new Date();
  const diff = now - date;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

function formatDateTime(date) {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfter = new Date(now);
  dayAfter.setDate(dayAfter.getDate() + 2);

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  if (date.toDateString() === now.toDateString()) {
    return `Today · ${timeStr}`;
  }
  if (date.toDateString() === tomorrow.toDateString()) {
    return `Tomorrow · ${timeStr}`;
  }
  if (date.toDateString() === dayAfter.toDateString()) {
    return `${date.toLocaleDateString("en-US", { weekday: "short" })} ${timeStr}`;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ status, tone }) {
  const colorMap = {
    purple: "bg-purple-100 text-purple-700 border-purple-200",
    blue: "bg-sky-100 text-sky-700 border-sky-200",
    yellow: "bg-amber-100 text-amber-700 border-amber-200",
    green: "bg-emerald-100 text-emerald-700 border-emerald-200",
    teal: "bg-teal-100 text-teal-700 border-teal-200",
    red: "bg-red-100 text-red-700 border-red-200",
    gray: "bg-slate-100 text-slate-700 border-slate-200",
  };
  const cls = colorMap[tone] || colorMap.blue;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${cls}`}>
      {status}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, onClick, trend }) {
  const trendColor = trend > 0 ? "text-emerald-600" : "text-red-600";
  const trendBg = trend > 0 ? "bg-emerald-50" : "bg-red-50";

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full group focus:outline-none"
    >
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-5 flex flex-col gap-3 transition-all duration-300 hover:border-sky-300 hover:shadow-lg hover:scale-[1.02] group-focus-visible:ring-2 group-focus-visible:ring-sky-400 group-focus-visible:ring-offset-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">
              {value}
            </div>
          </div>
          <div className="h-12 w-12 rounded-xl bg-sky-400 flex items-center justify-center">
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
        
        {trend && (
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold w-fit ${trendBg} ${trendColor}`}>
            {trend > 0 ? (
              <TrendingUp className="h-3.5 w-3.5" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5" />
            )}
            <span>{Math.abs(trend)}% vs last week</span>
          </div>
        )}
      </div>
    </button>
  );
}

function FilterDropdown({ options, value, onChange, label }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-all ${
          value 
            ? "bg-sky-50 text-sky-700 border-sky-200 shadow-sm" 
            : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
        }`}
      >
        <Filter className="h-3.5 w-3.5" />
        {label}: {value || "All"}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-20 overflow-hidden">
            <div className="py-1">
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-2.5 text-xs transition ${
                  !value 
                    ? "bg-sky-50 text-sky-700 font-semibold" 
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                All
              </button>
              {options.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-xs transition ${
                    value === option 
                      ? "bg-sky-50 text-sky-700 font-semibold" 
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [totalApplicationsCount, setTotalApplicationsCount] = useState(0);
  const [products, setProducts] = useState([]);
  const [totalProductsCount, setTotalProductsCount] = useState(0);
  const [activeProductsCount, setActiveProductsCount] = useState(0);
  const [scheduledProducts, setScheduledProducts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [userCounts, setUserCounts] = useState({ borrowers: 0, lenders: 0 });

  // Product filters
  // eslint-disable-next-line no-unused-vars
  const [productSearchTerm, setProductSearchTerm] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [productCategoryFilter, setProductCategoryFilter] = useState("");
  
  // rowsToShow for Recently Added
  const [rowsToShow, setRowsToShow] = useState(5);

  // Activity type filter (dropdown selection)
  const [activityTypeFilter, setActivityTypeFilter] = useState("");

  // Loading states
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Selected date for scheduled products (default to today)
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  
  // Fetch data on mount and when refreshing
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  // Refetch scheduled products when selected date changes
  useEffect(() => {
    fetchScheduledProducts();
  }, [selectedDate]);
  
  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      // Fetch recent applications (all applications, sorted by modified date)
      // No applicant filter for admin - show all applications
      const appsResponse = await listApplications({
        order_by: "modified desc",
        limit_page_length: 20,
        fields: JSON.stringify([
          "name",
          "status",
          "loan_amount",
          "modified",
          "creation",
          "team_member",
          "first_name",
          "surname",
          "email",
        ]),
      });
      // Handle different response formats
      let appsData = [];
      if (Array.isArray(appsResponse)) {
        appsData = appsResponse;
      } else if (appsResponse?.data && Array.isArray(appsResponse.data)) {
        appsData = appsResponse.data;
      } else if (appsResponse?.message && Array.isArray(appsResponse.message)) {
        appsData = appsResponse.message;
      }
      
      const normalizedApps = appsData
        .map(normalizeApplication)
        .filter(Boolean);
      setApplications(normalizedApps.slice(0, 6));

      // Fetch true DB total applications count for KPI (not capped by recent-table limit)
      try {
        const appsCountPayload = await apiGet("/api/method/frappe.client.get_count", {
          doctype: "Applications",
        });
        setTotalApplicationsCount(Number(extractMessage(appsCountPayload) || 0));
      } catch (countErr) {
        console.error("Error fetching total applications count:", countErr);
        setTotalApplicationsCount(normalizedApps.length);
      }

      // Fetch recently added products (all products, sorted by creation)
      const productsResponse = await getMortgageProducts({
        // Use a high limit so KPIs see the full product set (matches Product Management KPIs)
        limit_page_length: 10000,
      });
      const productsData = Array.isArray(productsResponse) ? productsResponse : [];
      // Sort by creation date (most recent first)
      const sortedProducts = [...productsData].sort((a, b) => {
        const dateA = new Date(a._raw?.creation || a._raw?.modified || 0);
        const dateB = new Date(b._raw?.creation || b._raw?.modified || 0);
        return dateB - dateA;
      });
      setProducts(sortedProducts);

      // Total / Active products KPIs — combined BTL + IF from get_product_summary?type=all
      try {
        const allSummary = await getAllProductSummary();
        const summaryTotal = Number(allSummary?.total_products);
        const summaryActive = Number(allSummary?.active_products);
        if (Number.isFinite(summaryTotal) && summaryTotal >= 0) {
          setTotalProductsCount(summaryTotal);
        } else {
          setTotalProductsCount(sortedProducts.length);
        }
        if (Number.isFinite(summaryActive) && summaryActive >= 0) {
          setActiveProductsCount(summaryActive);
        } else {
          setActiveProductsCount(
            sortedProducts.filter((p) => {
              const s = String(p.status || "").toLowerCase();
              return s === "active" || s === "live" || s === "approved";
            }).length
          );
        }
      } catch (countErr) {
        console.error("Error fetching product summary KPI:", countErr);
        // Fallback: Mortgage Product doctype counts (may exclude IF products)
        try {
          const [totalPayload, activePayload] = await Promise.all([
            apiGet("/api/method/frappe.client.get_count", {
              doctype: "Mortgage Product",
            }),
            apiGet("/api/method/frappe.client.get_count", {
              doctype: "Mortgage Product",
              filters: JSON.stringify([["status", "=", "Active"]]),
            }),
          ]);
          setTotalProductsCount(Number(extractMessage(totalPayload) || 0));
          setActiveProductsCount(Number(extractMessage(activePayload) || 0));
        } catch {
          setTotalProductsCount(sortedProducts.length);
          setActiveProductsCount(0);
        }
      }
      
      // Fetch scheduled products will be done separately when selectedDate changes
      await fetchScheduledProducts();

      // Recent activity for the whole system (admin)
      const activityRes = await getRecentActivities({ limit: 50, for_admin: 1 });
      const activityItems = activityRes?.ok && Array.isArray(activityRes.items) ? activityRes.items : [];
      const normalizedActivity = activityItems.map((item) => {
        const eventLower = (item.event || "").toLowerCase();
        const msgLower = (item.message || "").toLowerCase();
        const text = `${eventLower} ${msgLower}`;
        let type = "other";
        // Application stages (check specific stages first, then generic application)
        if (text.includes("pre-enquiry") || text.includes("pre enquiry")) type = "preenquiry";
        else if ((text.includes("enquiry") && !text.includes("pre"))) type = "enquiry";
        else if (text.includes("completion") || text.includes("completed")) type = "completion";
        else if (text.includes("valuation")) type = "valuation";
        else if (text.includes("legal") || text.includes("legals")) type = "legal";
        else if (text.includes("approval")) type = "approval";
        else if (text.includes("application")) type = "application";
        else if ((text.includes("account") && text.includes("created")) || text.includes("account created")) type = "account_created";
        else if ((text.includes("product") && text.includes("created")) || text.includes("product created")) type = "product_created";
        else if ((text.includes("product") && text.includes("updated")) || text.includes("product updated")) type = "product_updated";
        else if (text.includes("product")) type = "product";
        return {
          id: item.name,
          title: item.event || item.message || "Activity",
          actor: item.actor || "—",
          time: item.timestamp ? new Date(item.timestamp) : new Date(),
          message: item.message,
          ref_title: item.ref_title,
          ref_name: item.ref_name,
          type,
        };
      });
      setActivity(normalizedActivity);

      // Fetch total borrowers and lenders (admin-level counts)
      try {
        const [borrowersRes, lendersRes] = await Promise.all([
          adminListUsers({ role: "Borrower", limit_page_length: 1, exclude_system_manager: 1 }),
          adminListUsers({ role: "Lender", limit_page_length: 1, exclude_system_manager: 1 }),
        ]);
        setUserCounts({
          borrowers: borrowersRes?.total ?? 0,
          lenders: lendersRes?.total ?? 0,
        });
      } catch (err) {
        console.error("Error fetching user counts:", err);
      }
      
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };
  
  // Fetch scheduled products for a specific date
  const fetchScheduledProducts = async () => {
    try {
      // Fetch all scheduled products
      const scheduledResponse = await getMortgageProducts({
        product_schedule: 1,
        limit_page_length: 100,
      });
      const scheduledData = Array.isArray(scheduledResponse) ? scheduledResponse : [];
      
      // Filter scheduled products by date_time for the selected date only
      const selectedDateStart = new Date(selectedDate);
      selectedDateStart.setHours(0, 0, 0, 0);
      const selectedDateEnd = new Date(selectedDate);
      selectedDateEnd.setHours(23, 59, 59, 999);
      
      const filteredScheduled = scheduledData
        .filter(product => {
          const dateTime = product._raw?.date_time;
          if (!dateTime) return false;
          
          const scheduleDate = new Date(dateTime);
          
          // Show only products scheduled for the selected date
          return scheduleDate >= selectedDateStart && scheduleDate <= selectedDateEnd;
        })
        .map(product => {
          const dateTime = product._raw?.date_time;
          return {
            id: product._id || product._raw?.name || "SCH-UNKNOWN",
            lender: product._raw?.lender_name || product._raw?.lender || "Unknown Lender",
            name: product.product || product._raw?.product_name || "Unknown Product",
            date: dateTime ? new Date(dateTime) : new Date(),
          };
        })
        .sort((a, b) => a.date - b.date);
      
      setScheduledProducts(filteredScheduled);
    } catch (error) {
      console.error("Error fetching scheduled products:", error);
      setScheduledProducts([]);
    }
  };

  // KPI calculations
  const kpiCards = useMemo(() => {
    const totalProducts = totalProductsCount || products.length;
    const totalBorrowers = userCounts.borrowers || 0;
    const totalApplications = totalApplicationsCount || applications.length;
    const totalLenders = userCounts.lenders || 0;

    return [
      {
        label: "Total Products",
        value: totalProducts.toString(),
        icon: Package,
        to: "/admin/products",
        trend: 12,
      },
      {
        label: "Active Products",
        value: (activeProductsCount || 0).toString(),
        icon: CheckCircle,
        to: "/admin/products",
        trend: 0,
      },
      {
        label: "Active Borrowers",
        value: totalBorrowers.toLocaleString(),
        icon: Users,
        to: "/admin/borrowers",
        trend: 8,
      },
      {
        label: "Applications",
        value: totalApplications.toString(),
        icon: ClipboardList,
        to: "/admin/applications",
        trend: -3,
      },
      {
        label: "Total Lenders",
        value: totalLenders.toString(),
        icon: Building2,
        to: "/admin/lenders",
        trend: 5,
      },
    ];
  }, [applications, totalApplicationsCount, products.length, totalProductsCount, activeProductsCount, userCounts]);

  // ✅ 10 most recent products (already sorted by creation date in fetchDashboardData)
  const recentProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];
    return products.slice(0, 10);
  }, [products]);

  // ✅ visible rows (start at 5, can expand up to 15 / total)
  const visibleProducts = useMemo(
    () => recentProducts.slice(0, rowsToShow),
    [recentProducts, rowsToShow]
  );

  const handleShowMore = () => {
    setRowsToShow((prev) =>
      Math.min(prev + 5, 15, recentProducts.length || 5)
    );
  };

  // Map dropdown label to activity type (stages + account/product filters)
  const ACTIVITY_FILTER_TO_TYPE = {
    "Pre-enquiry": "preenquiry",
    "Enquiry": "enquiry",
    "Application": "application",
    "Approval": "approval",
    "Valuation": "valuation",
    "Legal": "legal",
    "Completion": "completion",
    "Account created": "account_created",
    "Product": "product",
    "Product created": "product_created",
    "Product updated": "product_updated",
  };

  const filteredActivity = useMemo(() => {
    if (!activityTypeFilter) return activity;
    const type = ACTIVITY_FILTER_TO_TYPE[activityTypeFilter];
    if (!type) return activity;
    return activity.filter((a) => a.type === type);
  }, [activity, activityTypeFilter]);

  // Refresh data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchDashboardData();
  };

  // StatusBadge component for products
  const ProductStatusBadge = ({ status }) => {
    const s = String(status || "").toLowerCase();
    const isActive = s === "active" || s === "live" || s === "approved";
    const classes = isActive
      ? "bg-emerald-100 text-emerald-800"
      : "bg-rose-100 text-rose-800";

    return (
      <span
        className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold text-center ${classes}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <Hero
        title="Dashboard"
        subtitle="Welcome back! Here's your platform overview"
        right={
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-sky-600 bg-white rounded-xl hover:bg-sky-50 transition-all shadow-lg disabled:opacity-50 hover:scale-105"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh Data
          </button>
        }
      />

      {/* KPI row */}
      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <KpiCardSkeleton key={`kpi-skeleton-${i}`} />
          ))
        ) : (
          kpiCards.map((card) => (
            <StatCard
              key={card.label}
              label={card.label}
              value={card.value}
              icon={card.icon}
              trend={card.trend}
              onClick={() => navigate(card.to)}
            />
          ))
        )}
      </section>

      {/* Main grid */}
      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="xl:col-span-2 space-y-6">
          {/* Recent applications — design mirrors Diary (Application Calendar) */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-sky-600" />
                <h2 className="text-xs font-semibold text-slate-900">Recent Applications</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition"
                >
                  <RefreshCw className={`h-3 w-3 ${isRefreshing ? "animate-spin" : ""}`} />
                  Refresh
                </button>
                <button
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-sky-600 hover:text-sky-700 transition"
                  type="button"
                  onClick={() => navigate("/admin/calendar")}
                >
                  View all
                  <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">App ID</th>
                    <th className="px-3 py-2 text-left font-medium">Amount</th>
                    <th className="px-3 py-2 text-left font-medium">Status</th>
                    <th className="px-3 py-2 text-left font-medium">Assigned To</th>
                    <th className="px-3 py-2 text-left font-medium">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <TableSkeleton rows={6} cols={5} />
                  )}
                  
                  {!isLoading && applications.length === 0 && (
                    <tr>
                      <td className="px-3 py-8 text-center text-xs text-slate-500" colSpan={5}>
                        No applications found.
                      </td>
                    </tr>
                  )}
                  
                  {!isLoading && applications.slice(0, 6).map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition cursor-pointer"
                      onClick={() => navigate("/admin/calendar")}
                    >
                      <td className="px-3 py-2 font-medium text-slate-900">{row.id}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{money(row.loanAmount)}</td>
                      <td className="px-3 py-2">
                        <StatusBadge status={row.status} tone={row.statusTone} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {row.assignedTo && row.assignedTo !== "Unassigned" ? (
                            <>
                              <div className="h-5 w-5 bg-sky-100 rounded-full flex items-center justify-center">
                                <span className="text-[10px] font-medium text-sky-600">
                                  {row.assignedTo.split(" ").map((n) => n[0]).join("")}
                                </span>
                              </div>
                              <span className="text-[11px] text-slate-700">{row.assignedTo.split(" ")[0]}</span>
                            </>
                          ) : (
                            <span className="text-[11px] text-amber-600">Unassigned</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-500">{formatRelativeTime(row.modified || row.lastUpdated)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recently Added Products */}
          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-bold">Recently Added Products</h3>
              <p className="text-xs text-gray-500">
                Showing most recent products
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600">
                    <th className="text-left px-4 py-3 font-semibold">
                      PRODUCT
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">
                      CATEGORY
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">RATE</th>
                    <th className="text-center px-4 py-3 font-semibold whitespace-nowrap">
                      ARRANGEMENT FEE
                    </th>
                    <th className="text-left px-4 py-3 font-semibold">TERM</th>
                    <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">
                      AMOUNT RANGE
                    </th>
                    <th className="text-center px-4 py-3 font-semibold">
                      STATUS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <TableSkeleton rows={5} cols={7} />
                  )}

                  {!isLoading && visibleProducts.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-center text-slate-500" colSpan={7}>
                        No products found.
                      </td>
                    </tr>
                  )}

                  {!isLoading && visibleProducts.map((p, i) => {
                      const productName = p.product || `Product ${i + 1}`;
                      const category =
                        p.category ||
                        p._raw?.product_category ||
                        p._raw?.category ||
                        "—";
                      const rate = p.rate ?? "—";
                      const term = p.term ?? "—";
                      const range = p.range ?? "—";
                      const status = p.status ?? "Active";
                      const arrangementFee = p.arrangementFee ?? "—";

                      const handleClick = () => {
                        navigate(`/admin/products/${p._id || p.id}`);
                      };

                      return (
                        <tr
                          key={p._id || productName}
                          className="border-t border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={handleClick}
                        >
                          <td className="px-4 py-3 font-medium text-gray-900">
                            {productName}
                          </td>
                          <td className="px-4 py-3">
                            <TypeBadge t={category} />
                          </td>
                          <td className="px-4 py-3">{rate}</td>
                          <td className="px-4 py-3 text-center tabular-nums">
                            {arrangementFee}
                          </td>
                          <td className="px-4 py-3">{term}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {range}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center">
                              <ProductStatusBadge status={status} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                </tbody>
              </table>
            </div>

            {!isLoading &&
              recentProducts.length > 5 &&
              rowsToShow < Math.min(15, recentProducts.length) && (
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={handleShowMore}
                    className="text-xs px-3 py-1.5 rounded-full border border-sky-200 text-sky-600 hover:bg-sky-50 transition-colors"
                  >
                    Show more
                  </button>
                </div>
              )}
          </Card>

          {/* Scheduled products */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-amber-50 to-white px-5 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-amber-500 flex items-center justify-center">
                    <Calendar className="h-4 w-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-slate-900">
                    Scheduled Products
                  </h2>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full font-semibold">
                  <Clock className="h-3 w-3" />
                  From lender portal
                </div>
              </div>
              
              {/* Date selector */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-600">
                  Show products for:
                </label>
                <input
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    newDate.setHours(0, 0, 0, 0);
                    setSelectedDate(newDate);
                  }}
                  className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none"
                />
                {selectedDate.toDateString() === new Date().toDateString() && (
                  <span className="text-xs text-amber-600 font-medium">(Today)</span>
                )}
              </div>
            </div>
            
            <div className="p-4 space-y-3">
              {isLoading && (
                <>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={`skeleton-sched-${i}`} className="h-20 bg-amber-50 rounded-xl animate-pulse" />
                  ))}
                </>
              )}
              
              {!isLoading && scheduledProducts.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-500">
                  <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  No scheduled products found.
                </div>
              )}
              
              {!isLoading && scheduledProducts.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 hover:border-amber-300 hover:bg-amber-50/50 transition-all"
                >
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-slate-500 mb-1">{item.id}</div>
                    <div className="text-sm font-bold text-slate-900 mb-1">
                      {item.name}
                    </div>
                    <div className="text-xs text-slate-600 font-medium">{item.lender}</div>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-200 rounded-lg">
                    <Calendar className="h-3.5 w-3.5 text-amber-700" />
                    <span className="text-xs font-bold text-amber-700">
                      {formatDateTime(item.date)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column: recent activity - filter by type via dropdown */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col max-h-[50vh]">
          <div className="bg-gradient-to-r from-emerald-50 to-white px-5 py-4 border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-emerald-500 flex items-center justify-center">
                  <Activity className="h-4 w-4 text-white" />
                </div>
                <h2 className="text-base font-bold text-slate-900">
                  Recent Activity
                </h2>
              </div>
              <FilterDropdown
                label="Type"
                value={activityTypeFilter}
                onChange={setActivityTypeFilter}
                options={["Pre-enquiry", "Enquiry", "Application", "Approval", "Valuation", "Legal", "Completion", "Account created", "Product", "Product created", "Product updated"]}
              />
            </div>
          </div>
          
          <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
            {filteredActivity.length > 0 ? (
              filteredActivity.map((a) => (
                <div key={a.id} className="flex gap-3 group">
                  <div className="mt-1 h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-200">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-emerald-600 transition">
                      {a.title}
                    </div>
                    {a.message && (
                      <div className="text-xs text-slate-600 mt-0.5">
                        {a.message}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-1">
                      <span className="font-medium">{a.actor}</span>
                      <span className="text-slate-400">•</span>
                      <span>{formatRelativeTime(a.time)}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-sm text-slate-500">
                <AlertCircle className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                No activity found
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}